import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../../assets/css/dashboard.scss";
import DownIcon from "../../assets/images/dashboard/DownIcon.svg";
import {
  BoostSvg,
  ComSvg,
  ConfigSvg,
  DashboardSvg,
  EventSvg,
  ModerationSvg,
  PostSvg,
  StatSvg,
  StorySvg,
  SubscriptionSvg,
  TaskManagement,
  UniSvg,
  UserSvg,
  OrganizationSvg,
  ManagerIconSidebar
} from "../../assets/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetForm, setPage } from "../../redux/feature/AddProjectSlice";
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';
import UpgradePromptModal from '../common/UpgradePromptModal';
import { selectCurrentPlan } from '../../redux/selectors/authSelectors';

const FullScreenMenu = () => {
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Get current plan from Redux store
  const currentPlan = useSelector(selectCurrentPlan);

  // Check if user is super admin
  React.useEffect(() => {
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const isSuperAdminUser = userData?.user?.user?.role?.toLowerCase() === 'super_admin';
        setIsSuperAdmin(isSuperAdminUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Set active menu based on current route
  React.useEffect(() => {
    const currentPath = location.pathname;

    if (isSuperAdmin) {
      if (currentPath === '/') {
        setSelectedMenu(1); // Home
      } else if (currentPath === '/super-admin/projects' || currentPath.includes('/super-admin/project-details')) {
        setSelectedMenu(2); // Projects
      } else if (currentPath === '/super-admin/organizations') {
        setSelectedMenu(3); // Organizations
      } else if (currentPath === '/super-admin/managers') {
        setSelectedMenu(4); // Managers
      }
    } else {
      // For regular admin, set active based on route
      if (currentPath === '/') {
        setSelectedMenu(1); // Home
      } else if (currentPath.includes('/view-project') || currentPath.includes('/add-project') || currentPath.includes('/project-details') || currentPath.includes('/project-progress')) {
        setSelectedMenu(2); // Project
      } else if (currentPath.includes('/view-worker') || currentPath.includes('/add-worker') || currentPath.includes('/worker-details') || currentPath.includes('/worker-progress')) {
        setSelectedMenu(3); // Worker
      } else if (currentPath.includes('/view-tasks')) {
        setSelectedMenu(12); // Task Management
      } else if (currentPath.includes('/view-vendor') || currentPath.includes('/add-vendor')) {
        setSelectedMenu(9); // Vendor Management
      } else if (currentPath.includes('/expenses') || currentPath.includes('/expense-details')) {
        setSelectedMenu(5); // Expense
      } else if (currentPath.includes('/organization')) {
        setSelectedMenu(10); // Organization
      }
    }
  }, [location.pathname, isSuperAdmin]);

  const handleSubMenuToggle = (id) => {
    setActiveSubMenu(activeSubMenu === id ? null : id);
  };
  console.log("currentPlan", currentPlan);
  const handleMenuClick = (item) => {
    if (!isFeatureAccessible(item.name)) {
      const featureMap = {
        'Home': 'advancedReporting',
        'Vendor Management': 'vendorManagement',
        'Expense': 'expenseManagement',
        'Chat': 'inAppChat',
        'Map': 'liveTracking',
      };

      const feature = featureMap[item.name];
      setRestrictedFeature({
        name: item.name,
        requiredPlan: FEATURE_REQUIRED_PLANS[feature]?.charAt(0).toUpperCase() +
          FEATURE_REQUIRED_PLANS[feature]?.slice(1) // Capitalize first letter
      });
      setShowUpgradeModal(true);
      return;
    }

    setSelectedMenu(item.id);
    setSelectedSubMenu(null);

    // For super admin, explicitly navigate
    if (isSuperAdmin && item.link) {
      navigate(item.link);
    }
  };

  const handleSubMenuClick = (menuId, subMenuId) => {
    setSelectedMenu(menuId);
    setSelectedSubMenu(subMenuId);
  };

  const handleAddProjectClick = () => {
    dispatch(setPage(1));
    dispatch(resetForm());
    navigate("/add-project");
  };

  const isFeatureAccessible = (menuItem) => {
    // Super admin has access to everything
    if (isSuperAdmin) return true;

    // Map menu items to feature names
    const featureMap = {
      'Home': 'advancedReporting',
      'Vendor Management': 'vendorManagement',
      'Expense': 'expenseManagement',
      'Chat': 'inAppChat',
      'Map': 'liveTracking',
    };

    const feature = featureMap[menuItem];
    if (!feature) return true; // If no mapping exists, allow access

    const requiredPlan = FEATURE_REQUIRED_PLANS[feature];
    if (!requiredPlan) return true; // If no plan requirement, allow access

    // Check plan levels
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };

    return planLevels[currentPlan] >= planLevels[requiredPlan];
  };

  const adminMenuData = [
    {
      id: 1,
      name: "Home",
      link: "/",
      icon: DashboardSvg,
    },
    {
      id: 2,
      name: "Project",
      link: "/view-project",
      icon: UserSvg,
      submenu: [
        {
          id: 1,
          name: "View Project",
          link: "/view-project",
        },
        {
          id: 2,
          name: "Add Project",
          link: "/add-project",
        },
        // {
        //   id: 3,
        //   name: "Worker Project Allocation",
        //   link: "/project-allocation",
        // },
      ],
    },
    {
      id: 3,
      name: "Worker",
      link: "#",
      icon: UniSvg,
      submenu: [
        {
          id: 1,
          name: "View Worker",
          link: "/view-worker",
        },
        {
          id: 2,
          name: "Add Worker",
          link: "/add-worker",
        },
      ],
    },
    {
      id: 12,
      name: "Task Management",
      link: "/view-tasks",
      icon: TaskManagement,
    },
    {
      id: 9,
      name: "Vendor Management",
      link: "#",
      icon: UniSvg,
      submenu: [
        {
          id: 1,
          name: "View Vendor",
          link: "/view-vendor",
        },
        {
          id: 2,
          name: "Add Vendor",
          link: "/add-vendor",
        },
      ],
    },
    {
      id: 5,
      name: "Expense",
      link: "/expenses",
      icon: PostSvg,
    },
    // {
    //   id: 6,
    //   name: "Map",
    //   link: "/map",
    //   icon: EventSvg,
    // },
    // {
    //   id: 7,
    //   name: "Chat",
    //   link: "/chat",
    //   icon: ComSvg,
    // },
    // {
    //   id: 8,
    //   name: "Report",
    //   link: "/stories",
    //   icon: StorySvg,
    // },
    // {
    //   id: 9,
    //   name: "Task Management",
    //   link: "/view-tasks",
    //   icon: TaskManagement,
    // },
    // {
    //   id: 8,
    //   name: "Profile",
    //   link: "/profile",
    //   icon: BoostSvg,
    // },
    {
      id: 10,
      name: "Organization",
      link: "/organization",
      icon: UniSvg,
    },
  ];

  const superAdminMenuData = [
    {
      id: 1,
      name: "Home",
      link: "/",
      icon: DashboardSvg,
    },
    {
      id: 2,
      name: "Projects",
      link: "/super-admin/projects",
      icon: UserSvg,
    },
    {
      id: 4,
      name: "Managers",
      link: "/super-admin/managers",
      icon: ManagerIconSidebar,
    },
    {
      id: 5,
      name: "Subscription Management",
      link: "/super-admin/subscription-management",
      icon: SubscriptionSvg,
    },
  ];

  const menuData = isSuperAdmin ? superAdminMenuData : adminMenuData;

  const renderSubMenu = (menuId, subMenu) => {
    return (
      <>
        {subMenu?.map((item) => (
          <NavLink
            to={item.link}
            key={item.id}
            className={`sub-menu1-top-padding  py-3 my-1 pl-5 left-drop-down d-flex align-items-center 
                    ${selectedSubMenu === item.id && selectedMenu === menuId
                ? "font-semibold text-[14px] text-[#1E3A8A] dashboard-main-hover "
                : "font-semibold text-[14px] text-[#4B5563]"
              }`}
            onClick={(e) => {
              if (!isFeatureAccessible(item.name)) {
                e.preventDefault();
                handleSubMenuClick(menuId, item.id);
              } else {
                handleSubMenuClick(menuId, item.id);
                if (item.name === "Add Project") {
                  handleAddProjectClick();
                }
              }
            }}
          >
            {item.name}
          </NavLink>
        ))}
      </>
    );
  };

  return (
    <div className="hover-effect h-100 mt-2">
      {menuData.map((item) => (
        <div
          key={item.id}
          className={`menu-item border-bottom-custome ${!isFeatureAccessible(item.name) ? 'opacity-50' : ''
            }`}
          style={{ color: "#535353", userSelect: "none" }}
        >
          {item?.submenu ? (
            <div
              className={`d-flex align-items-center w-100 py-3 w-100 dashboard-main-hover cursor-pointer justify-content-center px-3 ${selectedMenu === item.id ? "selected-menu" : ""
                }`}
              style={selectedMenu === item.id ? { backgroundColor: '#EEF0F6' } : {}}
              onClick={(e) => {
                if (!isFeatureAccessible(item.name)) {
                  e.preventDefault();
                  handleMenuClick(item);
                } else {
                  handleSubMenuToggle(item.id);
                  handleMenuClick(item);
                }
              }}
            >
              <div
                className={`w-[46px] h-[40px] rounded-[100%] flex justify-center items-center ${selectedMenu === item.id ? "bg-white " : "bg-[#EEF0F6]"
                  }`}
              >
                {item.icon}
              </div>

              <p
                className={`mb-0 pl-lg-3 pl-1  w-100 ${selectedMenu === item.id
                  ? "font-semibold text-[14px] text-[#1E3A8A]"
                  : "font-semibold text-[14px] text-[#4B5563]"
                  }`}
              >
                {item.name}
              </p>

              {item.submenu && (
                <div>
                  <img
                    className="size-small-screen"
                    onClick={() => handleSubMenuToggle(item.id)}
                    src={DownIcon}
                    alt=""
                    style={{
                      transform:
                        activeSubMenu === item.id
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <NavLink
              to={item.link}
              className={`d-flex align-items-center py-3 w-100 dashboard-main-hover justify-content-center px-3 ${selectedMenu === item.id ? "selected-menu" : ""
                }`}
              style={selectedMenu === item.id ? { backgroundColor: '#EEF0F6' } : {}}
              onClick={(e) => {
                if (!isFeatureAccessible(item.name)) {
                  e.preventDefault();
                  handleMenuClick(item);
                } else {
                  handleMenuClick(item);
                }
              }}
            >
              <div
                className={`w-[46px] h-[40px] rounded-[100%] flex justify-center items-center ${selectedMenu === item.id ? "bg-white " : "bg-[#EEF0F6]"
                  }`}
              >
                {item.icon}
              </div>

              <p
                className={`mb-0 pl-lg-3 pl-1  text-left w-100 ${selectedMenu === item.id
                  ? "font-semibold text-[14px] text-[#1E3A8A]"
                  : "font-semibold text-[14px] text-[#4B5563]"
                  }`}
              >
                {item.name}
              </p>
            </NavLink>
          )}

          {activeSubMenu === item.id &&
            item.submenu &&
            renderSubMenu(item.id, item.submenu)}
        </div>
      ))}

      {/* Hide upgrade modal for super admin */}
      {!isSuperAdmin && (
        <UpgradePromptModal
          show={showUpgradeModal}
          handleClose={() => setShowUpgradeModal(false)}
          featureName={restrictedFeature?.name}
          requiredPlan={restrictedFeature?.requiredPlan}
        />
      )}
    </div>
  );
};

export default FullScreenMenu;


